import { useEffect, useRef } from 'react';
import { Button } from './Button.tsx';
import { WarningTriangleIcon, InfoCircleIcon } from './icons.tsx';
import styles from './ConfirmDialog.module.css';

interface ConfirmDialogProps {
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel?: string;
  secondaryConfirmLabel?: string;
  onSecondaryConfirm?: () => void;
  variant?: 'primary' | 'danger';
  confirming?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  cancelLabel = 'Annuleren',
  secondaryConfirmLabel,
  onSecondaryConfirm,
  variant = 'primary',
  confirming = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !confirming) onCancel();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirming, onCancel]);

  return (
    <div
      className={styles.overlay}
      onClick={() => { if (!confirming) onCancel(); }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
    >
      <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
        <div className={`icon-wrap ${variant === 'danger' ? 'icon-wrap-danger' : 'icon-wrap-primary'}`}>
          {variant === 'danger'
            ? <WarningTriangleIcon size={24} />
            : <InfoCircleIcon size={24} />}
        </div>
        <h2 id="confirm-dialog-title" className={styles.title}>{title}</h2>
        <p className={styles.message}>{message}</p>
        <div className={styles.actions}>
          <Button ref={cancelRef} variant="ghost" onClick={onCancel} disabled={confirming}>{cancelLabel}</Button>
          {secondaryConfirmLabel && onSecondaryConfirm && (
            <Button variant="ghost" onClick={onSecondaryConfirm} disabled={confirming}>{secondaryConfirmLabel}</Button>
          )}
          <Button variant={variant === 'danger' ? 'danger' : 'primary'} onClick={onConfirm} disabled={confirming}>{confirmLabel}</Button>
        </div>
      </div>
    </div>
  );
}
